import React from "react";
import { useRouter } from "next/router";
import { Check, Star, Zap } from "lucide-react";

const PricingCardsCLM = () => {
  const router = useRouter();
  const annualPlan = {
    name: "Annual Plan",
    price: "$499",
    period: "month",
    billedAs: "billed as $6000/year",
    popular: true,
    features: {
      core: {
        documents: "Unlimited",
        forms: "Unlimited",
        template: "Unlimited",
      },
      clmTools: [
        "Smart Contract Editor",
        "Approval Workflows",
        "Renewal Reminders",
        "Clause Library",
        "Version History & Change Logs",
        "Clickwrap & Policy hosting",
        "and more...",
      ],
      signatureFeatures: [
        "Legally binding eSignatures",
        "OTP & Password Protection",
        "Bulk Send",
        "Bulk Download",
        "and more...",
      ],
      aiTools: [
        "Certius AI",
        "AI Drafting & Redlining",
        "Clause Suggestions",
        "Risk Flagging",
      ],
      other: [
        "One-Click Migration from other platforms like DocuSign",
        "Google Drive, Dropbox, Hubspot, Pipedrive, GoHighLevel and other CRM integrations",
        "Stripe integration",
        "Unlimited Members",
        "Folder Permissions",
        "Workspace Collaboration",
        "Priority Support",
        "Personal Onboarding Assistance",
        "Custom feature requests development",
        "Help Center & Chat",
        "Audit Trail",
        "Certificate of Completion",
        "Role-based Access",
        "White-labeled Emails & URLs",
        "Logos and themes",
        "and more...",
      ],
    },
  };

  const quarterlyPlan = {
    name: "Quarterly Plan",
    price: "$699",
    period: "quarter",
    billedAs: "billed quarterly",
    popular: false,
    features: {
      core: {
        documents: "Unlimited",
        forms: "Unlimited",
        template: "Unlimited",
      },
      included: [
        "CLM Tools",
        "Signature Features",
        "AI Tools",
        "Easy Migration",
        "Integrations",
        "Team Access",
        "Support",
        "Audit & Security",
        "Custom Branding",
      ],
    },
  };

  const FeatureList = ({ features, title }) => (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="space-y-2">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-600 text-left">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const CoreFeatures = ({ core }) => (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">
        Core Features
      </h4>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Documents</span>
          <span className="text-sm font-medium text-gray-900">
            {core.documents}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Forms</span>
          <span className="text-sm font-medium text-gray-900">
            {core.forms}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Templates</span>
          <span className="text-sm font-medium text-gray-900">
            {core.template}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-4 pb-12">
      <div className="max-w-7xl mx-auto">
        {/* <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">CLM Pricing</h1>
          <p className="text-xl text-gray-600">Choose the perfect plan for your contract management needs</p>
        </div> */}

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Annual Plan */}
          <div className="relative bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {annualPlan.popular && (
              <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-bl-lg">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4" />
                  <span className="text-sm font-medium">Most Popular</span>
                </div>
              </div>
            )}

            <div className="p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {annualPlan.name}
                </h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-5xl font-bold text-gray-900">
                    {annualPlan.price}
                  </span>
                  <span className="text-xl text-gray-600 ml-2">
                    /{annualPlan.period}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{annualPlan.billedAs}</p>
              </div>

              <button
                onClick={() => router.push(`/clm/request-demo`)}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 mb-8"
              >
                Book a Demo
              </button>

              <div className="space-y-6">
                <CoreFeatures core={annualPlan.features.core} />
                <FeatureList
                  features={annualPlan.features.clmTools}
                  title="CLM Tools"
                />
                <FeatureList
                  features={annualPlan.features.signatureFeatures}
                  title="Signature Features"
                />
                <FeatureList
                  features={annualPlan.features.aiTools}
                  title="AI Tools"
                />
                <FeatureList
                  features={annualPlan.features.other}
                  title="Additional Features"
                />
              </div>
            </div>
          </div>

          {/* Quarterly Plan */}
          <div className="flex flex-col gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {quarterlyPlan.name}
                  </h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold text-gray-900">
                      {quarterlyPlan.price}
                    </span>
                    <span className="text-xl text-gray-600 ml-2">
                      /{quarterlyPlan.period}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {quarterlyPlan.billedAs}
                  </p>
                </div>

                <button
                  onClick={() => router.push(`/clm/request-demo`)}
                  className="w-full bg-gray-900 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-all duration-200 transform hover:scale-105 mb-8"
                >
                  Book a Demo
                </button>

                <div className="space-y-6">
                  <CoreFeatures core={quarterlyPlan.features.core} />

                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                    <div className="flex items-center mb-4">
                      <Zap className="w-5 h-5 text-green-600 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        Everything Included
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Get access to all premium features available in the annual
                      plan
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {quarterlyPlan.features.included.map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> All features from the Annual Plan
                      are included in the Quarterly Plan with the same
                      functionality and unlimited usage.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Enterprise Security & Compliance
                </h2>
                <p className="text-lg text-gray-600">
                  Trusted by 10,000+ Businesses Worldwide
                </p>
              </div>

              <div className="grid gap-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {`Single Sign-On (SSO)`}
                    </h3>
                  </div>
                  {/* <p className="text-gray-700 mb-4">
                    Enhance security with enterprise-grade single sign-on
                    capabilities
                  </p> */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        Seamless user authentication
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        Centralized access management
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        Reduced password fatigue
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Compliance Ready
                    </h3>
                  </div>
                  {/* <p className="text-gray-700 mb-4">
                    Meet the highest industry standards for data protection and
                    security
                  </p> */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        SOC 2 Type II compliant
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        SOC 2 certified
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        ISO 27001 GDPR compliant
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security & Compliance Section */}

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Need help choosing the right plan?
          </p>
          <button
            onClick={() => router.push(`/clm/request-demo`)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Contact our sales team
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingCardsCLM;
